import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as arActions from '@features/auto-redemption-settings/store/actions';
import * as autoRedeemActions from '@features/auto-redemption-settings/store/actions/auto-redemption-settings.actions';
import { SubmitCashback } from '@features/cashback/store/actions/cashback.actions';
import { EverydaySpendActions } from '@features/everyday-spend/store/actions/everyday-spend.actions';
import {
  DeliveryDetailsActionTypes,
  UpdateDeliveryDetailsSuccess,
} from '@features/order-checkout/actions/delivery-details.actions';
import {
  CreateOrderActionTypes,
  CreateOrderSuccess,
} from '@features/order-checkout/actions/order-create.actions';
import { TransferPoints } from '@features/points-transfer/store/actions/points-transfer.actions';
import * as tcActions from '@features/transfer-connect-v2/store/actions/checkout.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AutoRedemptionUpdate,
  MFAProductType,
  MFARedemptionType,
  MFARedirectUrlMap,
  OneTimeRedemptionCashBack,
  OneTimeRedemptionEverydaySpend,
  OneTimeRedemptionPointsTransfer,
} from '@rd/core/mfa/types';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { MFA_CONFIG } from '@rd/core/tokens/mfa.token';
import {
  AutoRedemptionSettingType,
  PeriodAutoRedemptionSettingStrategyOptions,
} from '@rd/types/auto-redemption-setting.type';
import { Order } from '@rd/types/order.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, merge, Observable } from 'rxjs';
import { filter, map, mergeAll, tap } from 'rxjs/operators';
import * as actions from '../actions/mfa.actions';

@Injectable()
export class MFAEffects {
  constructor(
    @Inject(MFA_CONFIG) private mfaConfig: MFARedirectUrlMap,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions,
    private guardHouseService: GuardhouseService,
  ) {}

  // utils
  private shoppingCartUrlVerified = this.mfaConfig.shoppingCart.rdRedirectUrl;
  private shoppingCartUrlFailed = this.mfaConfig.shoppingCart.ghRedirectUrl;

  private cashbackOneTimeRedemptionUrlVerified =
    this.mfaConfig.cashbackOneTimeRedemption.rdRedirectUrl;
  private cashbackOneTimeRedemptionUrlFailed =
    this.mfaConfig.cashbackOneTimeRedemption.ghRedirectUrl;

  private pointsTransferOneTimeRedemptionUrlVerified =
    this.mfaConfig.pointsTransferOneTimeRedemption.rdRedirectUrl;
  private pointsTransferOneTimeRedemptionUrlFailed =
    this.mfaConfig.pointsTransferOneTimeRedemption.ghRedirectUrl;

  private everydaySpendOneTimeRedemptionUrlVerified =
    this.mfaConfig.everydaySpendOneTimeRedemption.rdRedirectUrl;
  private everydaySpendOneTimeRedemptionUrlFailed =
    this.mfaConfig.everydaySpendOneTimeRedemption.ghRedirectUrl;

  private digitalGCAutoRedemptionVerified =
    this.mfaConfig.digitalGiftCardAutoRedemption.rdRedirectUrl;
  private digitalGCAutoRedemptionFailed =
    this.mfaConfig.digitalGiftCardAutoRedemption.ghRedirectUrl;

  private physicalGCAutoRedemptionVerified =
    this.mfaConfig.physicalGiftCardAutoRedemption.rdRedirectUrl;
  private physicalGCAutoRedemptionFailed =
    this.mfaConfig.physicalGiftCardAutoRedemption.ghRedirectUrl;

  private pointsTransferAutoRedemptionVerified =
    this.mfaConfig.pointsTransferAutoRedemption.rdRedirectUrl;
  private pointsTransferAutoRedemptionFailed =
    this.mfaConfig.pointsTransferAutoRedemption.ghRedirectUrl;

  private everydaySpendAutoRedemptionVerified =
    this.mfaConfig.everydaySpendAutoRedemption.rdRedirectUrl;
  private everydaySpendAutoRedemptionFailed =
    this.mfaConfig.everydaySpendAutoRedemption.ghRedirectUrl;

  private cashbackAutoRedemptionVerified =
    this.mfaConfig.cashBackAutoRedemption.rdRedirectUrl;
  private cashbackAutoRedemptionFailed =
    this.mfaConfig.cashBackAutoRedemption.ghRedirectUrl;

  private autoRedemptionUpdateUrlVerified =
    this.mfaConfig.AutoRedemptionUpdate.rdRedirectUrl;
  private autoRedemptionUpdateUrlFailed =
    this.mfaConfig.AutoRedemptionUpdate.ghRedirectUrl;

  redemptionFlows$ = merge([
    this.actions$.pipe(
      ofType(actions.MFAActionTypes.INITIATE_ONE_TIME_REDEMPTION_CASHBACK),
    ),
    this.actions$.pipe(
      ofType(
        actions.MFAActionTypes.INITIATE_ONE_TIME_REDEMPTION_POINTS_TRANSFER,
      ),
    ),
    this.actions$.pipe(
      ofType(
        actions.MFAActionTypes.INITIATE_ONE_TIME_REDEMPTION_EVERYDAY_SPEND,
      ),
    ),
    this.actions$.pipe(ofType(actions.MFAActionTypes.INITIATE_AUTO_REDEMPTION)),
    this.actions$.pipe(
      ofType(actions.MFAActionTypes.INITIATE_AUTO_REDEMPTION_UPDATE),
    ),
    this.actions$.pipe(
      ofType(actions.MFAActionTypes.INITIATE_SHOPPING_CART_REDEMPTION),
    ),
  ]).pipe(mergeAll());

  createOrderSuccess$: Observable<Order> = this.actions$.pipe(
    ofType(CreateOrderActionTypes.CREATE_ORDER_CHECKOUT_SUCCESS),
    map(
      (orderAction: ReturnType<typeof CreateOrderSuccess>) => orderAction.order,
    ),
  );

  updateOrderDeliverySuccess$: Observable<Order> = this.actions$.pipe(
    ofType(DeliveryDetailsActionTypes.UPDATE_DELIVERY_DETAILS_SUCCESS),
    map(
      (
        updateDeliveryOrderAction: ReturnType<
          typeof UpdateDeliveryDetailsSuccess
        >,
      ) => updateDeliveryOrderAction.order,
    ),
  );

  // order checkout flow has two different instances, creating order and updating order
  // this returns the correct order data
  shoppingCartOrder$: Observable<Order> = combineLatest([
    this.createOrderSuccess$,
    this.updateOrderDeliverySuccess$,
  ]).pipe(
    map(([orderFromOrderCreated, orderFromDeliveryUpdated]: any) => {
      if (orderFromOrderCreated && !orderFromDeliveryUpdated) {
        return orderFromOrderCreated;
      }

      if (orderFromOrderCreated && orderFromDeliveryUpdated) {
        if (orderFromOrderCreated.id === orderFromDeliveryUpdated.id) {
          return orderFromDeliveryUpdated;
        } else {
          return orderFromOrderCreated;
        }
      }
    }),
  );

  // Auto redemption
  initiateAutoRedemptionUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        autoRedeemActions.AutoRedemptionSettingTypes
          .UPDATE_AUTO_REDEMPTION_SETTING,
      ),
      map(
        (
          updateAutoRedemption: autoRedeemActions.UpdateAutoRedemptionSetting,
        ) => {
          const { id, pointsRequired, strategy, strategyOptions } =
            updateAutoRedemption.payload;

          const artRedemptionCashBackData: AutoRedemptionUpdate = {
            verifiedRedirectUrl:
              this.autoRedemptionUpdateUrlVerified ?? this.router.url,
            data: {
              id,
              pointsRequired,
              strategy,
              strategyOptions:
                strategyOptions as PeriodAutoRedemptionSettingStrategyOptions,
            },
            product: MFAProductType.AutoRedemptionUpdate,
            type: MFARedemptionType.AutoRedemption,
          };

          return actions.InitiateAutoRedemptionUpdate({
            arData: artRedemptionCashBackData,
          });
        },
      ),
    ),
  );

  handleAutoRedemptionUpdate$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(actions.RedirectMFA)),
        this.redemptionFlows$,
      ]).pipe(
        filter(
          (
            autoRedemptionUpdateActions: [
              actions.RedirectMFAReturnType,
              actions.InitiateAutoRedemptionUpdateReturnType,
            ],
          ) => autoRedemptionUpdateActions.every(isNotNull),
        ),
        filter(
          ([_, redemptionFlows]) =>
            redemptionFlows.type ===
            actions.MFAActionTypes.INITIATE_AUTO_REDEMPTION_UPDATE,
        ),
        tap(([redirectAction, initiateAutoRedemptionUpdate]) => {
          this.guardHouseService.redirectToMFAUrl(
            {
              ...initiateAutoRedemptionUpdate.arData,
            },
            this.autoRedemptionUpdateUrlFailed ?? this.router.url,
            redirectAction.level,
          );
        }),
      ),
    { dispatch: false },
  );

  initiateAutoRedemption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        arActions.AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING,
      ),
      map((createSettingAction: arActions.CreateAutoRedemptionSetting) =>
        actions.InitiateAutoRedemption({
          arData: createSettingAction.payload,
          isEverydaySpend: createSettingAction.isEverydaySpend,
        }),
      ),
    ),
  );

  handleAutoRedemption = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(actions.RedirectMFA)),
        this.redemptionFlows$,
      ]).pipe(
        filter(
          (
            autoRedemptionActions: [
              actions.RedirectMFAReturnType,
              actions.InitiateAutoRedemptionReturnType,
            ],
          ) => autoRedemptionActions.every(isNotNull),
        ),
        filter(
          ([_, redemptionFlows]) =>
            redemptionFlows.type ===
            actions.MFAActionTypes.INITIATE_AUTO_REDEMPTION,
        ),
        tap(([redirectAction, autoRedemptionAction]) => {
          switch (autoRedemptionAction.arData.type) {
            case AutoRedemptionSettingType.Cash:
              this.guardHouseService.redirectToMFAUrl(
                {
                  data: autoRedemptionAction.arData,
                  type: MFARedemptionType.AutoRedemption,
                  product: MFAProductType.Cashback,
                  verifiedRedirectUrl:
                    this.cashbackAutoRedemptionVerified ?? this.router.url,
                },
                this.cashbackAutoRedemptionFailed ?? '',
                redirectAction.level,
              );
              break;
            case AutoRedemptionSettingType.DigitalGiftCard:
              this.digitalGCAutoRedemptionFailed =
                this.digitalGCAutoRedemptionFailed ??
                `/gift_card/checkout/${autoRedemptionAction.arData.product_id}`;

              this.guardHouseService.redirectToMFAUrl(
                {
                  data: autoRedemptionAction.arData,
                  type: MFARedemptionType.AutoRedemption,
                  product: MFAProductType.PointsTransfer,
                  verifiedRedirectUrl:
                    this.digitalGCAutoRedemptionVerified ?? this.router.url,
                },
                this.digitalGCAutoRedemptionFailed,
                redirectAction.level,
              );
              break;
            case AutoRedemptionSettingType.PhysicalGiftCard:
              this.physicalGCAutoRedemptionFailed =
                this.physicalGCAutoRedemptionFailed ??
                `/gift_card/checkout/${autoRedemptionAction.arData.product_id}`;
              this.guardHouseService.redirectToMFAUrl(
                {
                  data: autoRedemptionAction.arData,
                  type: MFARedemptionType.AutoRedemption,
                  product: MFAProductType.PointsTransfer,
                  verifiedRedirectUrl:
                    this.physicalGCAutoRedemptionVerified ?? this.router.url,
                },
                this.physicalGCAutoRedemptionFailed,
                redirectAction.level,
              );
              break;
            case AutoRedemptionSettingType.PointsTransfer:
              this.pointsTransferAutoRedemptionFailed =
                this.pointsTransferAutoRedemptionFailed ??
                `/transfer/checkout/${autoRedemptionAction.arData.product_id}`;

              const everydaySpendAutoRedemptionFailed =
                this.everydaySpendAutoRedemptionFailed ??
                `/everyday_rewards/checkout/${autoRedemptionAction.arData.product_id}`;

              const failedRedirectUrl = autoRedemptionAction.isEverydaySpend
                ? everydaySpendAutoRedemptionFailed
                : this.pointsTransferAutoRedemptionFailed;

              const verifiedRedirectUrl = autoRedemptionAction.isEverydaySpend
                ? this.everydaySpendAutoRedemptionVerified
                : this.pointsTransferAutoRedemptionVerified;

              this.guardHouseService.redirectToMFAUrl(
                {
                  data: autoRedemptionAction.arData,
                  type: MFARedemptionType.AutoRedemption,
                  product: MFAProductType.PointsTransfer,
                  verifiedRedirectUrl: verifiedRedirectUrl ?? this.router.url,
                  isEverydaySpend: autoRedemptionAction.isEverydaySpend,
                },
                failedRedirectUrl,
                redirectAction.level,
              );
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  // one time redemption points transfer
  initiateOneTimeRedemptionPointsTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tcActions.CheckoutActionTypes.SUBMIT_POINTS_TRANSFER),
      map((submitPointsTransferAction: tcActions.SubmitPointsTransfer) => {
        const { formData, loyaltyProgramId, promocode, promocodeUsage } =
          submitPointsTransferAction.payload;
        const { targetAmount, sourceAmount, membershipId } = formData;

        const oneTimeRedemptionPointsTransferData: OneTimeRedemptionPointsTransfer =
          {
            verifiedRedirectUrl:
              this.pointsTransferOneTimeRedemptionUrlVerified ??
              this.router.url,
            data: {
              loyaltyProgramId,
              membershipId,
              targetAmount,
              sourceAmount,
              promocode: promocode?.code,
              promocodeUsage,
            },
            product: MFAProductType.PointsTransfer,
            type: MFARedemptionType.OneTimeRedemtion,
          };

        return actions.InitiateOneTimeRedemptionPointsTransfer({
          otrData: oneTimeRedemptionPointsTransferData,
        });
      }),
    ),
  );

  // one time redemption points transfer for new points transfer module
  initiateOneTimeRedemptionPointsTransferNewModule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferPoints),
      map(({ formData, loyaltyProgramId }) => {
        const { targetAmount, sourceAmount, membershipId } = formData;

        const oneTimeRedemptionPointsTransferData: OneTimeRedemptionPointsTransfer =
          {
            verifiedRedirectUrl:
              this.pointsTransferOneTimeRedemptionUrlVerified ??
              this.router.url,
            data: {
              loyaltyProgramId,
              membershipId,
              targetAmount,
              sourceAmount,
            },
            product: MFAProductType.PointsTransfer,
            type: MFARedemptionType.OneTimeRedemtion,
          };

        return actions.InitiateOneTimeRedemptionPointsTransfer({
          otrData: oneTimeRedemptionPointsTransferData,
        });
      }),
    ),
  );

  handlePointsTransferOneTimeRedemption$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(actions.RedirectMFA)),
        this.redemptionFlows$,
      ]).pipe(
        filter(
          (
            oneTimeRedemptionActions: [
              actions.RedirectMFAReturnType,
              actions.InitiateOneTimeRedemptionPointsTransferReturnType,
            ],
          ) => oneTimeRedemptionActions.every(isNotNull),
        ),
        filter(
          ([_, redemptionFlows]) =>
            redemptionFlows.type ===
            actions.MFAActionTypes.INITIATE_ONE_TIME_REDEMPTION_POINTS_TRANSFER,
        ),
        tap(([redirectAction, oneTimeRedemptionPointsTransfer]) => {
          this.pointsTransferOneTimeRedemptionUrlFailed =
            this.pointsTransferOneTimeRedemptionUrlFailed ??
            `/transfer/checkout/${this.activatedRoute.snapshot.queryParamMap.get(
              'loyaltyProgramId',
            )}`;

          this.guardHouseService.redirectToMFAUrl(
            {
              ...oneTimeRedemptionPointsTransfer.otrData,
            },
            this.pointsTransferOneTimeRedemptionUrlFailed,
            redirectAction.level,
          );
        }),
      ),
    { dispatch: false },
  );

  // one time redemption cashback
  initiateOneTimeCashbackRedemption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmitCashback),
      map(({ formData, loyaltyProgramId, promocode, promocodeUsage }) => {
        const { sourceAmount: redemptionAmount, targetAmount: cashAmount } =
          formData;

        const oneTimeRedemptionCashBackData: OneTimeRedemptionCashBack = {
          verifiedRedirectUrl:
            this.cashbackOneTimeRedemptionUrlVerified ?? this.router.url,
          data: {
            cashAmount,
            loyaltyProgramId,
            redemptionAmount,
            promocode: promocode?.code,
            promocodeUsage,
          },
          product: MFAProductType.Cashback,
          type: MFARedemptionType.OneTimeRedemtion,
        };

        return actions.InitiateOneTimeRedemptionCashback({
          otrData: oneTimeRedemptionCashBackData,
        });
      }),
    ),
  );

  handleCashbackOneTimeRedemption$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(actions.RedirectMFA)),
        this.redemptionFlows$,
      ]).pipe(
        filter(
          (
            cashBackActions: [
              actions.RedirectMFAReturnType,
              actions.InitiateOneTimeRedemptionCashbackReturnType,
            ],
          ) => cashBackActions.every(isNotNull),
        ),
        filter(
          ([_, redemptionFlows]) =>
            redemptionFlows.type ===
            actions.MFAActionTypes.INITIATE_ONE_TIME_REDEMPTION_CASHBACK,
        ),
        tap(([redirectAction, oneTimeRedemptionCashback]) => {
          this.guardHouseService.redirectToMFAUrl(
            {
              ...oneTimeRedemptionCashback.otrData,
            },
            this.cashbackOneTimeRedemptionUrlFailed ?? this.router.url,
            redirectAction.level,
          );
        }),
      ),
    { dispatch: false },
  );

  // shopping cart
  initiateShoppingCartRedemption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateOrderActionTypes.CREATE_ORDER_CHECKOUT_SUCCESS),
      map(() => actions.InitiateShoppingCartRedemption()),
    ),
  );

  handleShoppingCartRedemption$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(actions.RedirectMFA)),
        this.redemptionFlows$,
        this.shoppingCartOrder$,
      ]).pipe(
        // This is required to prevent other redemptions from triggering this flow
        filter(
          (
            shoppingCartActions: [
              actions.RedirectMFAReturnType,
              actions.InitiateRedemptionActionType,
              Order,
            ],
          ) => shoppingCartActions.every(isNotNull),
        ),
        filter(
          ([_, redemptionFlows, __]) =>
            redemptionFlows.type ===
            actions.MFAActionTypes.INITIATE_SHOPPING_CART_REDEMPTION,
        ),
        tap(([mfaAction, _, shoppingCartOrder]) => {
          this.guardHouseService.redirectToMFAUrl(
            {
              type: MFARedemptionType.ShoppingCart,
              data: shoppingCartOrder,
              verifiedRedirectUrl:
                this.shoppingCartUrlVerified ?? this.router.url,
            },
            this.shoppingCartUrlFailed ?? this.router.url,
            mfaAction.level,
          );
        }),
      ),
    { dispatch: false },
  );

  // everyday spend
  // one time redemption points transfer for new points transfer module
  initiateOneTimeRedemptionEverydaySpend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EverydaySpendActions.submitPointsTransfer),
      map(({ formData, loyaltyProgramId }) => {
        const { targetAmount, sourceAmount, membershipId } = formData;

        const oneTimeRedemptionEverydaySpendData: OneTimeRedemptionEverydaySpend =
          {
            verifiedRedirectUrl:
              this.everydaySpendOneTimeRedemptionUrlVerified ?? this.router.url,
            data: {
              loyaltyProgramId,
              membershipId,
              targetAmount,
              sourceAmount,
            },
            product: MFAProductType.EverydaySpend,
            type: MFARedemptionType.OneTimeRedemtion,
          };

        return actions.initiateOneTimeRedemptionEverydaySpend({
          otrData: oneTimeRedemptionEverydaySpendData,
        });
      }),
    ),
  );

  handleEverydaySpendOneTimeRedemption$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(actions.RedirectMFA)),
        this.redemptionFlows$,
      ]).pipe(
        filter(
          (
            oneTimeRedemptionActions: [
              actions.RedirectMFAReturnType,
              actions.InitiateOneTimeRedemptionEverydaySpendReturnType,
            ],
          ) => oneTimeRedemptionActions.every(isNotNull),
        ),
        filter(
          ([_, redemptionFlows]) =>
            redemptionFlows.type ===
            actions.MFAActionTypes.INITIATE_ONE_TIME_REDEMPTION_EVERYDAY_SPEND,
        ),
        tap(([redirectAction, oneTimeRedemptionEverydaySpend]) => {
          this.everydaySpendOneTimeRedemptionUrlFailed =
            this.everydaySpendOneTimeRedemptionUrlFailed ??
            `/everyday_rewards/checkout/${this.activatedRoute.snapshot.queryParamMap.get(
              'loyaltyProgramId',
            )}`;

          this.guardHouseService.redirectToMFAUrl(
            {
              ...oneTimeRedemptionEverydaySpend.otrData,
            },
            this.everydaySpendOneTimeRedemptionUrlFailed,
            redirectAction.level,
          );
        }),
      ),
    { dispatch: false },
  );
}
